import { Injectable, Output } from '@angular/core';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { PermissionExpressionService } from './permission-expression.service';
import { UserService } from './user.service';
import { UserPermissionsService } from './userpermissions.service';

@Injectable({
	providedIn: 'root',
})
export class SubMenuHandlerService {
	private favoriteItems: Record<string, SubMenuItem[]> = {};
	private menuItems: Record<string, SubMenuItem[]> = {};
	private readonly baseDisabled = new WeakMap<SubMenuItem, (() => boolean) | undefined>();

	@Output()
	public favoriteItemsArray: SubMenuItem[] = [];

	@Output()
	public menuItemsArray: SubMenuItem[] = [];

	constructor(
		public userPermissionsService: UserPermissionsService,
		private permissionExpressionService: PermissionExpressionService,
		private userService: UserService,
	) {
		this.userService.accountsSubject$.subscribe(() => this.reevaluateAll());
		this.userPermissionsService.permissions$.subscribe(() => this.reevaluateAll());
	}

	private reevaluateAll(): void {
		this.favoriteItemsArray = this.favoriteItems && Object.keys(this.favoriteItems).length > 0 ? this.evaluateItems(this.favoriteItems) : [];
		this.menuItemsArray = this.menuItems && Object.keys(this.menuItems).length > 0 ? this.evaluateItems(this.menuItems) : [];
	}

	private evaluateItems(record: Record<string, SubMenuItem[]>): SubMenuItem[] {
		if (!record) return [];

		let items = Object.values(record).flat();

		items = items.filter((i) => {
			if (i.permissions && this.userPermissionsService) {
				if (i.disableOnMissingPermissions?.()) {
					const disabled = !this.permissionExpressionService.hasPermission(i.permissions());
					if (!this.baseDisabled.has(i)) this.baseDisabled.set(i, i.disabled);
					const original = this.baseDisabled.get(i);
					i.disabled = () => original?.() || disabled;
					return true;
				}

				return this.permissionExpressionService.hasPermission(i.permissions());
			}
			return true;
		});

		return items;
	}

	public setMenuItem(key: string, items: SubMenuItem | SubMenuItem[], override: boolean = false) {
		const data = Array.isArray(items) ? items : [items];

		if (!this.menuItems[key]) this.menuItems[key] = [];
		else if (!override) throw new Error(`Menu items with key ${key} already exists. Use addMenuItem to add more items.`);

		this.menuItems[key] = data;

		this.menuItemsArray = this.evaluateItems(this.menuItems);
	}

	public setFavoriteItem(key: string, items: SubMenuItem | SubMenuItem[], override: boolean = false) {
		const data = Array.isArray(items) ? items : [items];

		if (!this.favoriteItems[key]) this.favoriteItems[key] = [];
		else if (!override) throw new Error(`Favorite items with key ${key} already exists. Use addFavoriteItem to add more items.`);

		this.favoriteItems[key] = data;

		this.favoriteItemsArray = this.evaluateItems(this.favoriteItems);
	}

	public removeMenuItems(key: string) {
		delete this.menuItems[key];
		this.menuItemsArray = this.evaluateItems(this.menuItems);
	}

	public removeFavoriteItems(key: string) {
		delete this.favoriteItems[key];
		this.favoriteItemsArray = this.evaluateItems(this.favoriteItems);
	}

	public clearMenuItems() {
		this.menuItems = {};
		this.menuItemsArray = [];
	}

	public clearFavoriteItems() {
		this.favoriteItems = {};
		this.favoriteItemsArray = [];
	}

	public clearAll() {
		this.clearMenuItems();
		this.clearFavoriteItems();
	}
}
